import {
  ArrowLeftRight,
  ArrowRightLeft,
  Banknote,
  BadgeCheck,
  CalendarDays,
  CalendarHeart,
  ClipboardCheck,
  Clock4,
  FileEdit,
  Landmark,
  ListChecks,
  MapPinOff,
  MessageSquareQuote,
  NotebookPen,
  OctagonAlert,
  Package,
  Receipt,
  ScrollText,
  Smile,
  Timer,
  Trophy,
  UserMinus,
  UserSquare,
  type LucideIcon,
} from 'lucide-react';
import type { PendingActionSource } from '../../api/pendingActions';
import { parseApiUtc, parseApiDateOnly } from '@/utils/apiDate';
import { appDate } from '@/utils/appDateFormat';

// Presentation metadata per source. `label` is a flat key in the `requests`
// i18n namespace (keySeparator is false — literal strings).

interface SourceMeta {
  icon: LucideIcon;
  label: string;
}

const FALLBACK: SourceMeta = { icon: ClipboardCheck, label: 'Request' };

const SOURCE_META: Record<PendingActionSource, SourceMeta> = {
  Leave: { icon: CalendarDays, label: 'Leave' },
  ShiftSwap: { icon: ArrowLeftRight, label: 'Shift swap' },
  Claim: { icon: Receipt, label: 'Claim' },
  AttendanceCorrection: { icon: FileEdit, label: 'Correction' },
  ShortLeave: { icon: Timer, label: 'Short leave' },
  Encashment: { icon: Banknote, label: 'Encashment' },
  AssetRequest: { icon: Package, label: 'Asset' },
  ShiftSwapAcceptance: { icon: ArrowLeftRight, label: 'Swap for you' },
  Onboarding: { icon: ListChecks, label: 'Onboarding' },
  Terms: { icon: ScrollText, label: 'Terms' },
  Survey: { icon: Smile, label: 'Pulse survey' },
  ReturnToWork: { icon: ClipboardCheck, label: 'Return to work' },
  Referee: { icon: Trophy, label: 'Referee' },
  OutOfBoundsClock: { icon: MapPinOff, label: 'Out of bounds' },
  // Approval-wave sources. Keep this a TOTAL Record: a new member of the
  // PendingActionSource union must break the build here, not fall back to a
  // generic clipboard the user can't tell apart from a real one.
  InternalMovement: { icon: ArrowRightLeft, label: 'Internal movement' },
  EmployeeMovement: { icon: ArrowRightLeft, label: 'Employee movement' },
  PublicProfile: { icon: UserSquare, label: 'Public profile' },
  GpssaPending: { icon: Landmark, label: 'GPSSA contributions' },
  GoalBlocker: { icon: OctagonAlert, label: 'Goal blocker' },
  ReflectionResponse: { icon: MessageSquareQuote, label: 'Reflection response' },
  OvertimeApproval: { icon: Timer, label: 'Overtime' },
  ShiftPayApproval: { icon: Clock4, label: 'Hourly shift' },
  Offboarding: { icon: UserMinus, label: 'Offboarding' },
  PlatformTerms: { icon: ScrollText, label: 'Platform terms' },
  Reflection: { icon: NotebookPen, label: 'Reflection' },
  PerformanceSnapshot: { icon: BadgeCheck, label: 'Performance snapshot' },
  RestrictedHoliday: { icon: CalendarHeart, label: 'Restricted holidays' },
};

export function sourceMeta(source: string): SourceMeta {
  return SOURCE_META[source as PendingActionSource] ?? FALLBACK;
}

type TFunc = (key: string, opts?: Record<string, unknown>) => string;

/** True when two dates fall on the same calendar day (local time). */
function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * Compact date span in the system format ("19-07-2026" / "19-07-2026 – 20-07-2026"), mirroring the
 * server's cases. dd-mm-yyyy for every viewer (owner decision 2026-09-17), never the machine locale.
 */
function formatDateRange(startIso: string | undefined, endIso: string | undefined, _locale: string): string | undefined {
  if (!startIso) return undefined;
  // Calendar days (leave spans) — read textually; `new Date('2026-09-17')` is the previous day west of UTC.
  const start = parseApiDateOnly(startIso);
  if (!start) return undefined;
  const end = endIso ? parseApiDateOnly(endIso) : null;
  if (!end || Number.isNaN(end.getTime()) || isSameDay(start, end)) {
    return appDate(start);
  }
  return `${appDate(start)} – ${appDate(end)}`;
}

/**
 * The system-generated part of a Leave item's subtitle ("2 days · 19–20 Jul"),
 * localized on the client from the item's structured Meta (numberOfDays /
 * startDate / endDate). The leave TYPE is user data and stays server-provided
 * (the row's title). Returns undefined when the Meta is missing so the caller
 * can fall back to the server's English subtitle.
 */
export function leaveSubtitle(meta: Record<string, string> | undefined, locale: string, t: TFunc): string | undefined {
  if (!meta) return undefined;
  const range = formatDateRange(meta.startDate, meta.endDate, locale);
  if (!range) return undefined;
  const days = Number(meta.numberOfDays);
  const daysLabel =
    Number.isFinite(days) && days > 0
      ? days === 1
        ? t('1 day')
        : t('{{n}} days', { n: days.toLocaleString(locale) })
      : undefined;
  return daysLabel ? `${daysLabel} · ${range}` : range;
}

/**
 * Compact relative timestamp ("2m ago" / "3h ago" / "5d ago", older falls back
 * to a dd-mm-yyyy date). Mirrors the mobile inbox so both clients read the same.
 */
export function relativeTime(iso: string | undefined, locale: string, t: TFunc): string {
  if (!iso) return '';
  const when = parseApiUtc(iso);
  if (!when) return '';
  const diffMs = Date.now() - when.getTime();
  const minutes = Math.floor(diffMs / 60000);
  // {{n}} (not {{count}}) on purpose: `count` triggers i18next plural-suffix
  // lookups these flat keys don't provide.
  if (minutes < 1) return t('Just now');
  if (minutes < 60) return t('{{n}}m ago', { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('{{n}}h ago', { n: hours });
  const days = Math.floor(hours / 24);
  if (days <= 7) return t('{{n}}d ago', { n: days });
  return appDate(when);
}
